import { startAuthBootstrap } from './authBootstrap';
import { startConnectivity } from './connectivity';
import { startPrefsSync, type PrefsSyncOptions } from './prefsSync';
import type { AppStoreLike } from './types';

export { resolverConflictoPrefs } from './prefsSync';
export type { AppStoreLike } from './types';

/** Lo mínimo del `persistor` de redux-persist que hace falta para esperar la rehidratación. */
export interface PersistorLike {
  getState(): { bootstrapped: boolean };
  subscribe(listener: () => void): () => void;
}

export interface AppListenersOptions {
  /** Si se pasa, los listeners esperan a que el estado persistido termine de cargarse. */
  persistor?: PersistorLike;
  prefs?: PrefsSyncOptions;
}

const activos = new WeakMap<object, () => void>();

/**
 * Arranca los listeners de la app (conectividad, sesión y sincronización de preferencias). Idempotente por
 * store: llamarlo dos veces no duplica nada. Devuelve la función que los detiene (y permite reiniciarlos).
 */
export function startAppListeners(
  store: AppStoreLike,
  options: AppListenersOptions = {},
): () => void {
  const previo = activos.get(store);
  if (previo) return previo;

  let detenidos = false;
  let hijos: Array<() => void> = [];
  const arrancar = (): void => {
    if (detenidos) return;
    hijos = [
      startConnectivity(store),
      startAuthBootstrap(store),
      startPrefsSync(store, options.prefs),
    ];
  };

  const { persistor } = options;
  let dejarDeEsperar: (() => void) | null = null;
  if (!persistor || persistor.getState().bootstrapped) arrancar();
  else {
    dejarDeEsperar = persistor.subscribe(() => {
      if (!persistor.getState().bootstrapped) return;
      dejarDeEsperar?.();
      arrancar();
    });
  }

  const detener = (): void => {
    detenidos = true;
    dejarDeEsperar?.();
    hijos.forEach((h) => h());
    hijos = [];
    activos.delete(store);
  };
  activos.set(store, detener);
  return detener;
}
